import { useNavigate } from 'react-router';
import {
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Box,
  Typography,
} from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useEffect, useState } from 'react';
import { PDMenuItem } from '@/menu/menu.type';
import { useTranslation } from 'react-i18next';
import { useMenuContext } from '@contexts/menu';
import { useNotifications } from '@/contexts/notificationsCount';

type MenuItemRecursiveProps = {
  item: PDMenuItem;
  navigate: ReturnType<typeof useNavigate>;
  currentPath: string;
  first: boolean;
  submenu?: boolean;
};

export default function MenuItemRecursive({
  item,
  navigate,
  currentPath,
  first,
  submenu = false,
}: MenuItemRecursiveProps) {
  const { t } = useTranslation();
  const { activeMenuKey, setActiveMenuKey } = useMenuContext();
  const [manuallyToggled, setManuallyToggled] = useState(false);
  const { notifications } = useNotifications();

  const isActive =
    currentPath === item.path ||
    item.submenu?.some((sub) => currentPath === sub.path);

  const isOpen = activeMenuKey === item.name || (isActive && !manuallyToggled); // reabre automaticamente se URL ativa e não foi manualmente fechado

  const handleClick = () => {
    if (item.submenu?.length) {
      if (activeMenuKey === item.name) {
        setActiveMenuKey(null); // Fecha
        setManuallyToggled(true);
      } else {
        setActiveMenuKey(item.name); // Abre
        setManuallyToggled(false);
        if (item.path) navigate(item.path);
      }
    } else if (item.path) {
      navigate(item.path);
    }
  };

  useEffect(() => {
    if (isActive) {
      setActiveMenuKey(item.name);
      setManuallyToggled(false);
    }
  }, [currentPath]);

  return (
    <Box
      sx={{
        bgcolor: isActive
          ? 'primary.main'
          : isOpen
            ? '#00d16030'
            : 'transparent',
        ...(first && {
          margin: '10px',
          borderRadius: '10px',
          padding: '10px',
        }),
      }}
    >
      <ListItemButton
        onClick={handleClick}
        sx={{
          margin: 0,
          padding: '2px 0',
        }}
      >
        {item.icon && (
          <ListItemIcon
            sx={{
              color: isActive ? 'white !important' : 'text.secondary',
              minWidth: 28,
            }}
          >
            {item.icon}
          </ListItemIcon>
        )}
        <ListItemText
          primary={
            <Typography
              fontWeight={submenu ? (isActive ? 700 : 400) : 700}
              fontSize={14}
              color={isActive ? 'white !important' : 'text.secondary'}
            >
              {t(item.name)}
            </Typography>
          }
        />
        {item.badge && (
          <Box
            display={'flex'}
            alignItems={'center'}
            justifyContent={'center'}
            sx={{
              background: '#FFC300',
              borderRadius: 1,
              width: 20,
              height: 20,
              fontWeight: 'bold',
              fontSize: '10px',
            }}
          >
            {notifications[item.badgeIndex || 'default'] || 0}
          </Box>
        )}
        {item.submenu && (isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />)}
      </ListItemButton>

      {item.submenu && (
        <Collapse in={isOpen} timeout="auto" unmountOnExit>
          <Box sx={{ pl: 1.6 }}>
            {item.submenu
              .filter((subItem) => subItem.permission)
              .map((subItem) => (
                <MenuItemRecursive
                  key={subItem.name}
                  item={subItem}
                  navigate={navigate}
                  currentPath={currentPath}
                  first={false}
                  submenu
                />
              ))}
          </Box>
        </Collapse>
      )}
    </Box>
  );
}
